import {DestroyRef, Directive, inject, output} from "@angular/core";

/**
 * Hvor lenge vi venter på at et klikk skal bli til et dobbeltklikk før klikket regnes som et enkeltklikk.
 * Avveining: lang forsinkelse gir treg respons ved enkeltklikk, kort forsinkelse gjør at en langsom dobbeltklikk likevel gir enkeltklikk.
 * Operativsystemets dobbeltklikk-tid er typisk 500ms, men de fleste dobbeltklikker raskere enn 300ms.
 */
export const ENKELTKLIKK_FORSINKELSE_MS = 300;

/**
 * Emitterer 'enkeltklikk' kun dersom klikket ikke blir til et dobbeltklikk.
 * Nettleseren sender click, click og dblclick ved dobbeltklikk, så (click) alene kan ikke skille enkelt- og dobbeltklikk.
 * Direktivet utsetter derfor enkeltklikket i {@link ENKELTKLIKK_FORSINKELSE_MS} og avbryter det dersom det kommer et dobbeltklikk.
 * Klikk uten musehendelse-detalj (event.detail === 0, f.eks. tastatur eller programmatisk) behandles som enkeltklikk.
 *
 * Bruk:
 * <div (enkeltklikk)="vedEnkeltklikk($event)" (dblclick)="vedDobbeltklikk()">klikk meg</div>
 */
@Directive({
    selector: "[enkeltklikk]",
    standalone: true,
    host: {
        '(click)': 'vedKlikk($event)',
        '(dblclick)': 'avbrytVentendeKlikk()'
    }
})
export class EnkeltklikkDirektiv {

    enkeltklikk = output<MouseEvent>();

    private ventendeKlikk: ReturnType<typeof setTimeout> | undefined = undefined;

    constructor() {
        inject(DestroyRef).onDestroy(() => this.avbrytVentendeKlikk());
    }

    protected vedKlikk(event: MouseEvent) {
        if (event.detail >= 2) this.avbrytVentendeKlikk();
        else this.utsettKlikk(event);
    }

    private utsettKlikk(event: MouseEvent) {
        this.avbrytVentendeKlikk();
        this.ventendeKlikk = setTimeout(() => {
            this.ventendeKlikk = undefined;
            this.enkeltklikk.emit(event);
        }, ENKELTKLIKK_FORSINKELSE_MS);
    }

    protected avbrytVentendeKlikk() {
        if (this.ventendeKlikk === undefined) return;
        clearTimeout(this.ventendeKlikk);
        this.ventendeKlikk = undefined;
    }
}
